import { StatisticType } from "../types/dashboard.type";
import { OrganizationType } from "../types/organization.type";

const SITE_OPS_TYPES: OrganizationType[] = [OrganizationType.WASTE_OUTLET];

export interface DashboardStatDescriptor {
  stat: StatisticType;
  allowedTypes: OrganizationType[];
}

/**
 * Operational widgets on the ESG Monthly tab — gated by organization type.
 * ESG-only cards (waste diverted, utilities, carbon charts, …) stay in the ESG picklists.
 * `WASTE_DIVERSION_RATE` is omitted to avoid duplicating the ESG diversion card.
 */
export const ESG_OPERATIONAL_STAT_CATALOG: DashboardStatDescriptor[] = [
  { stat: StatisticType.FULL_PERCENTAGE, allowedTypes: SITE_OPS_TYPES },
  { stat: StatisticType.STORAGE_CAPACITY_UTILIZATION, allowedTypes: SITE_OPS_TYPES },
  { stat: StatisticType.EMERGENCY_COLLECTION_REQUESTS, allowedTypes: SITE_OPS_TYPES },
  { stat: StatisticType.INCOMING_WASTE_BREAKDOWN, allowedTypes: SITE_OPS_TYPES },
  { stat: StatisticType.AVERAGE_CONTAINER_FILL_RATE, allowedTypes: SITE_OPS_TYPES },
  { stat: StatisticType.WASTE_COMPACTION_EFFICIENCY, allowedTypes: SITE_OPS_TYPES },

  { stat: StatisticType.TRIPS_TODAY_COUNT, allowedTypes: [OrganizationType.TRUCK] },
  { stat: StatisticType.TRUCK_DRIVER_COUNT, allowedTypes: [OrganizationType.TRUCK] },
  { stat: StatisticType.AVERAGE_ROUTE_EFFICIENCY, allowedTypes: [OrganizationType.TRUCK] },
  { stat: StatisticType.FUEL_CONSUMPTION_PER_TRIP, allowedTypes: [OrganizationType.TRUCK] },
  { stat: StatisticType.DRIVER_PERFORMANCE_SCORE, allowedTypes: [OrganizationType.TRUCK] },
  { stat: StatisticType.MAINTENANCE_SCHEDULE_COMPLIANCE, allowedTypes: [OrganizationType.TRUCK] },
  { stat: StatisticType.TIME_FROM_COLLECTION_TO_DELIVERY, allowedTypes: [OrganizationType.TRUCK] },

  { stat: StatisticType.RECYCLER_APPETITE, allowedTypes: [OrganizationType.RECYCLER] },
  { stat: StatisticType.SUPPLY_PER_RECYCLER, allowedTypes: [OrganizationType.RECYCLER] },
  { stat: StatisticType.SUSTAINABLE_SUPPLY, allowedTypes: [OrganizationType.RECYCLER] },
  { stat: StatisticType.PROCESSING_CAPACITY_UTILIZATION, allowedTypes: [OrganizationType.RECYCLER] },
  { stat: StatisticType.QUALITY_COMPLIANCE_RATE, allowedTypes: [OrganizationType.RECYCLER] },
  { stat: StatisticType.REJECTED_WASTE_PERCENTAGE, allowedTypes: [OrganizationType.RECYCLER] },
  { stat: StatisticType.AVERAGE_PROCESSING_TIME, allowedTypes: [OrganizationType.RECYCLER] },

  {
    stat: StatisticType.WASTE_TYPE_BREAKDOWN,
    allowedTypes: [...SITE_OPS_TYPES, OrganizationType.RECYCLER],
  },
  {
    stat: StatisticType.AVERAGE_INCENTIVE_PER_KG,
    allowedTypes: [OrganizationType.WASTE_OUTLET, OrganizationType.RECYCLER],
  },

  { stat: StatisticType.TOTAL_REVENUE, allowedTypes: [OrganizationType.WASTE_OUTLET] },
  {
    stat: StatisticType.CUSTOMER_SATISFACTION_SCORE,
    allowedTypes: [OrganizationType.TRUCK, OrganizationType.RECYCLER],
  },
  {
    stat: StatisticType.COLLECTION_FREQUENCY,
    allowedTypes: [...SITE_OPS_TYPES, OrganizationType.TRUCK],
  },
  {
    stat: StatisticType.TIME_FROM_FULL_TO_COLLECTION,
    allowedTypes: [...SITE_OPS_TYPES, OrganizationType.TRUCK],
  },
  {
    stat: StatisticType.WASTE_QUALITY_RATING,
    allowedTypes: [...SITE_OPS_TYPES, OrganizationType.RECYCLER],
  },
];

export const OPERATIONAL_CHART_STATS: StatisticType[] = [
  StatisticType.INCOMING_WASTE_BREAKDOWN,
  StatisticType.WASTE_TYPE_BREAKDOWN,
  StatisticType.SUPPLY_PER_RECYCLER,
];

const OPERATIONAL_CHART_STAT_SET = new Set(OPERATIONAL_CHART_STATS);

export function isOperationalChartStat(stat: StatisticType): boolean {
  return OPERATIONAL_CHART_STAT_SET.has(stat);
}

export function getOperationalStatsForOrgType(
  orgType: OrganizationType,
): StatisticType[] {
  return ESG_OPERATIONAL_STAT_CATALOG.filter((d) =>
    d.allowedTypes.includes(orgType),
  ).map((d) => d.stat);
}

export function isOperationalStatAllowedForOrgType(
  stat: StatisticType,
  orgType: OrganizationType,
): boolean {
  return ESG_OPERATIONAL_STAT_CATALOG.some(
    (d) => d.stat === stat && d.allowedTypes.includes(orgType),
  );
}

/** Previous Monthly customize allowlist — used to merge newly added org-type stats. */
export const LEGACY_ESG_OPERATIONAL_ALLOWLIST: StatisticType[] = [
  StatisticType.AVERAGE_INCENTIVE_PER_KG,
  StatisticType.INCOMING_WASTE_BREAKDOWN,
];

export function newlyAddedOperationalStats(
  orgType: OrganizationType,
): StatisticType[] {
  const previous = new Set(LEGACY_ESG_OPERATIONAL_ALLOWLIST);
  return getOperationalStatsForOrgType(orgType).filter((stat) => !previous.has(stat));
}
